import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileQuestion,
  Loader2,
  AlertCircle,
  Download,
  Archive,
  Trash2,
  Clock,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Share2,
  Mail,
  Users,
  BookOpen,
  Pencil,
  Check,
  X
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSermons } from '../../hooks/useSermons';
import { generateStudyQuestions } from '../../lib/openai';
import { jsPDF } from 'jspdf';
import { format } from 'date-fns';

interface StudyQuestionSet {
  id: string;
  title: string;
  questions: string[];
  created_at: string;
  sermon: {
    title: string;
    scripture: string;
  };
  archived?: boolean;
}

type AudienceType = 'general' | 'youth' | 'children' | 'youngAdults' | 'theological';

export function StudyQuestions({ compact, defaultSermonId }: { compact?: boolean; defaultSermonId?: string } = {}) {
  const { sermons } = useSermons();
  const [selectedSermonId, setSelectedSermonId] = useState<string>(defaultSermonId ?? '');
  const [questionSets, setQuestionSets] = useState<StudyQuestionSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [expandedSets, setExpandedSets] = useState<Set<string>>(new Set());
  const [audience, setAudience] = useState<AudienceType>('general');

  // Inline editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftQuestions, setDraftQuestions] = useState<string[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (set: StudyQuestionSet) => {
    setDraftTitle(set.title);
    setDraftQuestions([...set.questions]);
    setEditingId(set.id);
    setExpandedSets(prev => new Set(prev).add(set.id));
  };

  const cancelEdit = () => { setEditingId(null); setDraftTitle(''); setDraftQuestions([]); };

  const saveEdit = async (id: string) => {
    setSavingEdit(true);
    try {
      const { error } = await supabase
        .from('study_questions')
        .update({ title: draftTitle, questions: draftQuestions })
        .eq('id', id);
      if (error) throw error;
      await loadQuestionSets();
      setEditingId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    loadQuestionSets();
  }, [showArchived]);

  const toggleExpanded = (id: string) => {
    setExpandedSets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const loadQuestionSets = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('study_questions')
        .select(`
          *,
          sermon:sermons!inner (
            title,
            scripture
          )
        `)
        .eq('user_id', user.id)
        .eq('archived', showArchived)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      
      // Filter out any question sets without valid sermon data
      const validQuestionSets = (data || []).filter(set => 
        set.sermon && set.sermon.title && set.sermon.scripture
      );
      
      setQuestionSets(validQuestionSets);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load question sets');
    } finally {
      setLoading(false);
    }
  };

  const generateQuestions = async () => {
    if (!selectedSermonId) return;

    try {
      setGenerating(true);
      setError(null);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      const { data: sermon, error: sermonError } = await supabase
        .from('sermons')
        .select('*')
        .eq('id', selectedSermonId)
        .single();

      if (sermonError) throw sermonError;
      if (!sermon) throw new Error('Sermon not found');

      // Pass the audience type to the generateStudyQuestions function
      const result = await generateStudyQuestions(sermon.content, audience);
      if (!result.success) {
        throw new Error(result.error || 'Failed to generate questions');
      }

      const questions = JSON.parse(result.content);

      const { data: newSet, error: insertError } = await supabase
        .from('study_questions')
        .insert([{
          user_id: user.id,
          sermon_id: selectedSermonId,
          title: `Study Guide: ${sermon.title}${audience !== 'general' ? ` (${getAudienceLabel(audience)})` : ''}`,
          questions,
          archived: false
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      await loadQuestionSets();
      setSelectedSermonId('');
      
      // Auto-expand the newly created set
      if (newSet) {
        setExpandedSets(prev => new Set(prev).add(newSet.id));
      }
    } catch (e) {
      console.error('Error generating questions:', e);
      setError(e instanceof Error ? e.message : 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const toggleArchive = async (id: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('study_questions')
        .update({ archived: !currentState })
        .eq('id', id);

      if (error) throw error;
      await loadQuestionSets();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive question set');
    }
  };

  const deleteQuestionSet = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this question set? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('study_questions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadQuestionSets();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete question set');
    }
  };

  const downloadPDF = (set: StudyQuestionSet) => {
    const doc = new jsPDF();
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const ml = 20;
    const mr = W - ml;
    const contentW = mr - ml;
    const lineH = 6.5;
    let y = 20;

    // ── HEADER ──────────────────────────────────────────────────────────────
    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(1.2);
    doc.line(ml, y, mr, y);
    y += 9;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(15, 23, 42);
    doc.text(set.title, W / 2, y, { align: 'center' });
    y += 11;

    // Sermon meta
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(71, 85, 105);
    const sermonTitleLines = doc.splitTextToSize(set.sermon.title, contentW);
    doc.text(sermonTitleLines, W / 2, y, { align: 'center' });
    y += sermonTitleLines.length * 6.5 + 3;

    if (set.sermon.scripture) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(148, 163, 184);
      doc.text(set.sermon.scripture, W / 2, y, { align: 'center' });
      y += 5;
    }

    y += 5;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.4);
    doc.line(ml, y, mr, y);
    y += 12;

    // ── QUESTIONS ────────────────────────────────────────────────────────────
    // Section header
    doc.setFillColor(30, 64, 175);
    doc.rect(ml - 5, y - 5.5, 3, 11, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 64, 175);
    doc.text('DISCUSSION QUESTIONS', ml + 1, y);
    y += 8;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(ml, y - 2, mr, y - 2);
    y += 8;

    doc.setTextColor(15, 23, 42);
    doc.setFontSize(11);

    set.questions.forEach((question, index) => {
      const qLines = doc.splitTextToSize(`${index + 1}.  ${question}`, contentW - 4);
      const blockH = qLines.length * lineH + 10;
      if (y + blockH > H - 28) { doc.addPage(); y = ml; }

      doc.setFont('helvetica', 'bold');
      doc.text(qLines[0], ml + 4, y);
      if (qLines.length > 1) {
        doc.setFont('helvetica', 'normal');
        for (let j = 1; j < qLines.length; j++) {
          y += lineH;
          doc.text(qLines[j], ml + 10, y);
        }
      }
      y += lineH + 6;
    });

    // Footer on every page
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(ml, H - 16, mr, H - 16);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('SermonBuild', ml, H - 10);
      doc.text(`${i} / ${pages}`, W / 2, H - 10, { align: 'center' });
    }

    const fileName = set.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    doc.save(`${fileName}.pdf`);
  };

  const shareViaEmail = (set: StudyQuestionSet) => {
    const subject = encodeURIComponent(set.title);
    const body = encodeURIComponent(`
Study Questions for "${set.sermon.title}"
Scripture: ${set.sermon.scripture}

Discussion Questions:
${set.questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}
    `);

    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Helper function to get audience label
  const getAudienceLabel = (audienceType: AudienceType): string => {
    switch (audienceType) {
      case 'youth':
        return 'Youth';
      case 'children':
        return 'Children';
      case 'youngAdults':
        return 'Young Adults/College';
      case 'theological':
        return 'Theological Study';
      default:
        return 'General';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-6"}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {!compact && <FileQuestion className="h-5 w-5 text-midnight" />}
          <h2 className={compact ? "text-sm font-semibold text-midnight" : "text-xl font-semibold text-midnight"}>Study Questions</h2>
        </div>
        <button
          onClick={() => setShowArchived(!showArchived)}
          className={`flex items-center px-4 py-2 rounded-lg ${
            showArchived 
              ? 'bg-electric text-white hover:bg-electric/90'
              : 'bg-sand/20 text-midnight hover:bg-sand/30'
          }`}
        >
          {showArchived ? (
            <>
              <Clock className="h-4 w-4 mr-2" />
              Show Active
            </>
          ) : (
            <>
              <Archive className="h-4 w-4 mr-2" />
              Show Archived
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-crimson/10 text-crimson p-4 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {!showArchived && (
        <div className={`bg-white rounded-lg shadow-sm border border-sand/20 ${compact ? 'p-3' : 'p-6'}`}>
          <h3 className={`${compact ? 'text-sm' : 'text-lg'} font-medium text-midnight mb-4`}>Generate New Questions</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Select Sermon
              </label>
              <select
                value={selectedSermonId}
                onChange={e => setSelectedSermonId(e.target.value)}
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
              >
                <option value="">Choose a sermon...</option>
                {sermons.map(sermon => (
                  <option key={sermon.id} value={sermon.id}>
                    {sermon.title}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Target Audience
              </label>
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-slate" />
                <select
                  value={audience}
                  onChange={e => setAudience(e.target.value as AudienceType)}
                  className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                >
                  <option value="general">General Audience</option>
                  <option value="youth">Youth (Teenagers)</option>
                  <option value="children">Children</option>
                  <option value="youngAdults">Young Adults/College</option>
                  <option value="theological">Theological Study</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-slate">
                {audience === 'theological' ? (
                  <>
                    <BookOpen className="h-3 w-3 inline-block mr-1" />
                    In-depth questions requiring deeper scriptural study and theological reflection
                  </>
                ) : (
                  'Questions will be tailored to the selected audience while maintaining the sermon\'s context and integrity.'
                )}
              </p>
            </div>
            
            <button
              onClick={generateQuestions}
              disabled={!selectedSermonId || generating}
              className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 flex items-center"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Questions...
                </>
              ) : (
                'Generate Questions'
              )}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {questionSets.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-sand/20">
            <FileQuestion className="h-12 w-12 text-slate/50 mx-auto mb-4" />
            <p className="text-slate">
              {showArchived 
                ? 'No archived question sets found'
                : 'No question sets available. Generate some questions to get started!'}
            </p>
          </div>
        ) : (
          questionSets.map(set => {
            const isEditing = editingId === set.id;
            const isExpanded = expandedSets.has(set.id);

            return (
              <div
                key={set.id}
                className="bg-white rounded-lg shadow-sm border border-sand/20 overflow-hidden"
              >
                <div
                  className={`${compact ? 'p-3' : 'p-6'} cursor-pointer hover:bg-sand/5 transition-colors`}
                  onClick={() => !isEditing && toggleExpanded(set.id)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <input
                          value={draftTitle}
                          onChange={e => setDraftTitle(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          className={`w-full ${compact ? 'text-sm' : 'text-lg'} font-medium text-midnight border border-electric/50 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-electric mb-1`}
                        />
                      ) : (
                        <h3 className={`${compact ? 'text-sm' : 'text-lg'} font-medium text-midnight`}>{set.title}</h3>
                      )}
                      <div className="text-sm text-slate">
                        {set.sermon.title} • {set.sermon.scripture}
                      </div>
                      <div className="text-xs text-slate mt-1">
                        Created {format(new Date(set.created_at), 'MMM d, yyyy')}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => saveEdit(set.id)}
                            disabled={savingEdit}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-electric text-white text-sm font-semibold rounded-lg hover:bg-electric/90 disabled:opacity-50"
                          >
                            {savingEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-sand/20 text-slate text-sm font-semibold rounded-lg hover:bg-sand/40"
                          >
                            <X className="h-3.5 w-3.5" />
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(set)}
                            title="Edit questions"
                            className="p-2 text-slate hover:text-electric rounded-lg transition-colors"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => downloadPDF(set)}
                            className="flex items-center px-3 py-2 text-sm bg-sand/20 text-midnight rounded-lg hover:bg-sand/30"
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            PDF
                          </button>
                          <button
                            onClick={() => shareViaEmail(set)}
                            className="p-2 text-slate hover:text-electric rounded-lg transition-colors"
                            title="Share via Email"
                          >
                            <Mail className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => toggleArchive(set.id, !!set.archived)}
                            className="p-2 text-slate hover:text-electric rounded-lg transition-colors"
                            title={set.archived ? 'Restore' : 'Archive'}
                          >
                            {set.archived ? <CheckCircle className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => deleteQuestionSet(set.id)}
                            className="p-2 text-slate hover:text-crimson rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          {isExpanded
                            ? <ChevronUp className="h-5 w-5 text-slate" />
                            : <ChevronDown className="h-5 w-5 text-slate" />}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-sand/20 pt-4">
                    {isEditing ? (
                      <div className="space-y-2">
                        {draftQuestions.map((q, i) => (
                          <div key={i} className="flex gap-3 items-start">
                            <span className="text-sm font-semibold text-slate mt-2.5 w-6 shrink-0">{i + 1}.</span>
                            <textarea
                              value={q}
                              onChange={e => {
                                const updated = [...draftQuestions];
                                updated[i] = e.target.value;
                                setDraftQuestions(updated);
                              }}
                              rows={Math.max(2, Math.ceil(q.length / 80))}
                              className="flex-1 resize-y rounded-md border border-electric/50 bg-white px-2.5 py-1.5 text-sm text-midnight focus:outline-none focus:ring-2 focus:ring-electric leading-relaxed"
                            />
                          </div>
                        ))}
                        <div className="flex justify-end gap-2 pt-2 border-t border-sand/20 mt-4">
                          <button onClick={cancelEdit} className="px-4 py-2 text-sm text-slate rounded-lg hover:bg-sand/20">
                            Cancel
                          </button>
                          <button
                            onClick={() => saveEdit(set.id)}
                            disabled={savingEdit}
                            className="flex items-center gap-2 px-4 py-2 bg-electric text-white text-sm font-medium rounded-lg hover:bg-electric/90 disabled:opacity-50"
                          >
                            {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      <ol className="list-decimal pl-4 space-y-2">
                        {set.questions.map((question, index) => (
                          <li key={index} className="text-slate">{question}</li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}